import { autobind } from "@typed-decorators/autobind";
import classNames from "classnames";
import { KEY_SLASH } from "keycode-js";
import type { ContextType } from "react";
import { createRef, Component } from "react";
import onClickOutside from "react-onclickoutside";

import * as Routes from "../../generated/routes";
import { hasFocus } from "../../utils/hasFocus";
import { stringIsPresent } from "../../utils/stringIsPresent";
import type { HotKeyDefinition } from "../HotKey";
import { HotKey } from "../HotKey";
import { SearchBox } from "../SearchBox";

import { SearchBoxBottomPanel } from "./SearchBoxDropdown/SearchBoxBottomPanel";

import { HistoryListContainer } from "~/client/components/search/HistoryListContainer";
import { IncrementalSearchResultListContainer } from "~/client/components/search/IncrementalSearchResultListContainer";
import { AppContext } from "~/client/components/shared/AppContext";

enum Command {
  FOCUS,
}

const HotKeyDefinitions: ReadonlyArray<HotKeyDefinition<Command>> = [
  {
    keyCode: KEY_SLASH,
    command: Command.FOCUS,
    disabledIf: (_event) => hasFocus(document),
  },
];

// DEBUG:
// `debug=searchCompletion` keeps completion panel open
// `debug=completionPlaceholders` keeps completion placeholders shown

interface Props {
  intl: any;
}

interface State {
  query: string;
  hasFocus: boolean;
  isReady: boolean;
  isCompletionPanelOpen: boolean;
  isBrowsingHistoryPanelOpen: boolean;
}

@onClickOutside
export class HeaderNavigationSearch extends Component<Props, State> {
  static readonly contextType = AppContext;
  declare context: ContextType<typeof AppContext>;

  readonly formRef = createRef<HTMLFormElement>();

  debugKeepCompletionOpen = false;
  debugKeepCompletionPlaceholders = false;

  constructor(props) {
    super(props);

    this.state = {
      query: "",
      hasFocus: false,
      isReady: false,
      isCompletionPanelOpen: false,
      isBrowsingHistoryPanelOpen: false,
    };
  }

  componentDidMount() {
    this.setState({ isReady: true });

    this.debugKeepCompletionOpen = /\bdebug=searchCompletion\b/.test(window.location.href);
    this.debugKeepCompletionPlaceholders = /\bdebug=completionPlaceholders\b/.test(window.location.href);
  }

  @autobind
  handleClickOutside() {
    this.closeCompletion();
  }

  @autobind
  handleQueryChange(query: string) {
    this.setState({ query });

    if (stringIsPresent(query)) {
      this.fetchCompletion();
    } else {
      this.setState({
        isBrowsingHistoryPanelOpen: true,
      });
    }
  }

  fetchCompletion() {
    this.setState({
      isCompletionPanelOpen: true,
      isBrowsingHistoryPanelOpen: false,
    });

    if (this.debugKeepCompletionPlaceholders) {
      return;
    }
  }

  @autobind
  handleFocus() {
    this.setState({ hasFocus: true, isCompletionPanelOpen: true, isBrowsingHistoryPanelOpen: true });
  }

  @autobind
  handleBlur() {
    this.setState({ hasFocus: false });
  }

  closeCompletion() {
    if (this.debugKeepCompletionOpen) {
      return;
    }

    this.setState({ isCompletionPanelOpen: false, isBrowsingHistoryPanelOpen: false });
  }

  @autobind
  handleSearch() {
    if (this.state.isReady) {
      this.formRef.current!.submit();
      this.setState({ isReady: false });
    }
  }

  @autobind
  handleDispatch(command: Command) {
    switch (command) {
      case Command.FOCUS:
        this.handleFocus();
        return;
    }
  }

  @autobind
  handleCloseBrowsingHistoryPanel() {
    this.setState({ isBrowsingHistoryPanelOpen: false });
  }

  render() {
    return (
      <form action={Routes.searchPath()} method="get" ref={this.formRef}>
        <HotKey definitions={HotKeyDefinitions} onDispatch={this.handleDispatch} />
        <SearchBox
          name={"query"}
          containerClassName={classNames((this.state.isCompletionPanelOpen || this.state.hasFocus) && "has-focus")}
          disabled={!this.state.isReady}
          placeholder={this.props.intl.formatMessage({ id: "searchBox.search" })}
          query={this.state.query}
          hasFocus={this.state.hasFocus}
          onQueryChange={this.handleQueryChange}
          onFocus={this.handleFocus}
          onBlur={this.handleBlur}
        />

        {this.state.isCompletionPanelOpen && stringIsPresent(this.state.query) && (
          <SearchBoxBottomPanel>
            <IncrementalSearchResultListContainer query={this.state.query} onSearch={this.handleSearch} />
          </SearchBoxBottomPanel>
        )}
        {this.state.isBrowsingHistoryPanelOpen && (
          <SearchBoxBottomPanel>
            <HistoryListContainer onSearch={this.handleSearch} onClose={this.handleCloseBrowsingHistoryPanel} />
          </SearchBoxBottomPanel>
        )}
      </form>
    );
  }
}
